/**
 * Eksport przekroju geologicznego do DXF (EKSPERYMENTALNE)
 * Używa biblioteki @tarikjabiri/dxf
 *
 * Flaga DXF_EXPORT_ENABLED pozwala wyłączyć funkcjonalność.
 */

import {
  DxfWriter,
  point2d,
  point3d,
  Colors,
  Units,
  LWPolylineFlags,
  HatchBoundaryPaths,
  HatchPolylineBoundary,
  HatchPredefinedPatterns,
  vertex,
  pattern,
  aciHex,
} from '@tarikjabiri/dxf';

import {
  SVG_TOP_MARGIN,
  SVG_LEFT_MARGIN,
  BOREHOLE_HALF_WIDTH,
} from './constants';

import {
  getConnectionKey as getConnectionKeyUtil,
  getConnectionPointKey as getConnectionPointKeyUtil,
  getTotalThickness,
} from './svgHelpers';

import { getMaxDepth as getMaxDepthUtil } from './svgDimensions';

// ============================================================
// FLAGA ON/OFF — ustaw false żeby wyłączyć eksport DXF
// ============================================================
export const DXF_EXPORT_ENABLED = true;

// ============================================================
// ACI color table — mapowanie index → [R, G, B]
// Budujemy z funkcji aciHex() z biblioteki
// ============================================================
const ACI_RGB = [];
for (let i = 0; i <= 255; i++) {
  try {
    const hex = aciHex(i);
    if (hex && hex.length === 6) {
      ACI_RGB[i] = [
        parseInt(hex.substring(0, 2), 16),
        parseInt(hex.substring(2, 4), 16),
        parseInt(hex.substring(4, 6), 16),
      ];
    } else {
      ACI_RGB[i] = [255, 255, 255];
    }
  } catch {
    ACI_RGB[i] = [255, 255, 255];
  }
}

/**
 * Konwertuje kolor hex (#RRGGBB) na najbliższy ACI index (1-255)
 */
function hexToAci(hex) {
  if (!hex || typeof hex !== 'string') return 7; // white
  const h = hex.replace('#', '');
  if (h.length < 6) return 7;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);

  let bestIndex = 7;
  let bestDist = Infinity;

  for (let i = 1; i <= 255; i++) {
    const [ar, ag, ab] = ACI_RGB[i];
    const dist = (r - ar) ** 2 + (g - ag) ** 2 + (b - ab) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }
  return bestIndex;
}

// ============================================================
// Mapowanie wzorów geologicznych → HatchPredefinedPatterns
// ============================================================
const PATTERN_MAP = {
  'sand': HatchPredefinedPatterns.DOTS,
  'silty-sand': HatchPredefinedPatterns.DOTS,
  'clayey-sand': HatchPredefinedPatterns.DOTS,
  'gravel': HatchPredefinedPatterns.GRAVEL,
  'sandy-gravel': HatchPredefinedPatterns.GRAVEL,
  'clay': HatchPredefinedPatterns.LINE,
  'silt': HatchPredefinedPatterns.LINE,
  'sandy-clay': HatchPredefinedPatterns.CLAY,
  'rock': HatchPredefinedPatterns.CROSS,
  'peat': HatchPredefinedPatterns.ANSI31,
  'alluvium': HatchPredefinedPatterns.MUDST,
  'debris': HatchPredefinedPatterns.AR_SAND,
  // Legacy lens patterns
  'dots': HatchPredefinedPatterns.DOTS,
  'horizontal': HatchPredefinedPatterns.LINE,
  'diagonal': HatchPredefinedPatterns.ANSI31,
  'cross': HatchPredefinedPatterns.CROSS,
  'none': HatchPredefinedPatterns.SOLID,
};

function getHatchPattern(appPattern) {
  return PATTERN_MAP[appPattern] || HatchPredefinedPatterns.SOLID;
}

// ============================================================
// Konwersja współrzędnych SVG → metry DXF
// ============================================================

/** SVG pixel X → odległość w metrach */
function svgXToM(svgX, horizontalScale) {
  return (svgX - SVG_LEFT_MARGIN) / horizontalScale;
}

/** SVG pixel Y → rzędna (elevation) w metrach. Y_dxf rośnie w górę. */
function svgYToElev(svgY, maxDepth, pixelsPerMeter) {
  return maxDepth - (svgY - SVG_TOP_MARGIN) / pixelsPerMeter;
}

// ============================================================
// Aproksymacja krzywej Béziera kubicznej (C) na N odcinków
// ============================================================

/**
 * Bézier kubiczny z jednym "control point" (tak jak w createBezierPath):
 * cp1 = start + (control - start) * 0.5
 * cp2 = end + (control - end) * 0.5
 */
function sampleCubicBezier(x1, y1, x2, y2, controlPoint, numSegments = 20) {
  if (!controlPoint) {
    return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  }

  const cp1x = x1 + (controlPoint.x - x1) * 0.5;
  const cp1y = y1 + (controlPoint.y - y1) * 0.5;
  const cp2x = x2 + (controlPoint.x - x2) * 0.5;
  const cp2y = y2 + (controlPoint.y - y2) * 0.5;

  const points = [];
  for (let i = 0; i <= numSegments; i++) {
    const t = i / numSegments;
    const mt = 1 - t;
    const x = mt * mt * mt * x1 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x2;
    const y = mt * mt * mt * y1 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y2;
    points.push({ x, y });
  }
  return points;
}

// ============================================================
// Pomocnik: dodaj zamknięty LWPOLYLINE z hatcha
// ============================================================
function addClosedPolyWithHatch(dxf, meterPoints, layerName, colorNum, appPattern, hatchScale) {
  if (meterPoints.length < 3) return;

  // Outline
  dxf.addLWPolyline(
    meterPoints.map(p => ({ point: point2d(p.x, p.y) })),
    { flags: LWPolylineFlags.Closed, layerName, colorNumber: colorNum }
  );

  // Hatch
  try {
    const polyBoundary = new HatchPolylineBoundary();
    meterPoints.forEach(p => polyBoundary.add(vertex(p.x, p.y)));
    const boundary = new HatchBoundaryPaths();
    boundary.addPolylineBoundary(polyBoundary);

    const patternName = getHatchPattern(appPattern);
    const hatch = dxf.addHatch(
      boundary,
      pattern({ name: patternName, scale: hatchScale || 1 }),
      { layerName }
    );
    hatch.colorNumber = colorNum;
  } catch (e) {
    // Hatch może nie zadziałać dla niektórych kształtów — ignorujemy
    console.warn('DXF hatch error:', e.message);
  }
}

// ============================================================
// EKSPORT OTWORÓW
// ============================================================
function exportBoreholes(dxf, params) {
  const { boreholes, maxDepth, horizontalScale } = params;
  const halfWidthM = BOREHOLE_HALF_WIDTH / horizontalScale;

  boreholes.forEach(borehole => {
    const bx = borehole.distance;
    const boreholeTopElev = maxDepth - (maxDepth - borehole.depth);
    // boreholeTopElev = borehole.depth (rzędna terenu otworu)

    // Nazwa otworu
    dxf.addText(point3d(bx, boreholeTopElev + 1.5, 0), 0.6, borehole.name || 'Otwór', {
      layerName: 'WARSTWY_OPIS',
    });

    // Rzędna pod nazwą
    dxf.addText(point3d(bx, boreholeTopElev + 0.5, 0), 0.35, borehole.depth.toFixed(2), {
      layerName: 'WARSTWY_OPIS',
      colorNumber: 8, // gray
    });

    let cumulativeDepth = 0;

    borehole.layers.forEach(layer => {
      const layerTopElev = boreholeTopElev - cumulativeDepth;
      const layerBottomElev = layerTopElev - layer.thickness;
      cumulativeDepth += layer.thickness;

      const colorNum = hexToAci(layer.color);

      if (params.showBoreholeRectangles) {
        // Prostokąt warstwy
        const rectPts = [
          { x: bx - halfWidthM, y: layerTopElev },
          { x: bx + halfWidthM, y: layerTopElev },
          { x: bx + halfWidthM, y: layerBottomElev },
          { x: bx - halfWidthM, y: layerBottomElev },
        ];
        addClosedPolyWithHatch(dxf, rectPts, 'WARSTWY_WYPELNIENIE', colorNum, layer.pattern || 'none', halfWidthM * 0.3);

        // Kontur otworu
        dxf.addLWPolyline(
          rectPts.map(p => ({ point: point2d(p.x, p.y) })),
          { flags: LWPolylineFlags.Closed, layerName: 'OTWORY', colorNumber: 7 }
        );
      }

      // Etykiety
      const centerElev = (layerTopElev + layerBottomElev) / 2;
      const textH = Math.max(0.15, Math.min(0.4, layer.thickness * 0.25));

      if (params.showSoilName && layer.name) {
        dxf.addText(point3d(bx, centerElev, 0), textH, layer.name, {
          layerName: 'WARSTWY_OPIS',
          colorNumber: colorNum,
        });
      }

      if (params.showThickness) {
        dxf.addText(point3d(bx, centerElev - textH * 1.5, 0), textH * 0.7, `${layer.thickness}m`, {
          layerName: 'WARSTWY_OPIS',
          colorNumber: 8,
        });
      }

      if (params.showDepths) {
        dxf.addText(
          point3d(bx + halfWidthM + 0.3, layerBottomElev, 0),
          textH * 0.7,
          cumulativeDepth.toFixed(1),
          { layerName: 'GLEBOKOSCI', colorNumber: 8 }
        );
      }

      if (params.showSoilState && layer.soilState) {
        dxf.addText(
          point3d(bx - halfWidthM - 0.3, centerElev, 0),
          textH * 0.6,
          layer.soilState,
          { layerName: 'WARSTWY_OPIS', colorNumber: 8 }
        );
      }

      if (params.showGeotechnicalLayer && layer.geotechnicalLayer) {
        dxf.addText(
          point3d(bx - halfWidthM - 0.3, centerElev - textH, 0),
          textH * 0.6,
          layer.geotechnicalLayer,
          { layerName: 'WARSTWY_OPIS', colorNumber: 8 }
        );
      }
    });
  });
}

// ============================================================
// EKSPORT POŁĄCZEŃ (interpolacje Bézier)
// ============================================================
function exportConnections(dxf, params) {
  if (!params.connectLayers) return;

  const {
    maxDepth, pixelsPerMeter, horizontalScale,
    interpolationPoints, connectionPointAdjustments,
  } = params;

  const connections = params.findLayerConnections();

  connections.forEach(conn => {
    const x1svg = SVG_LEFT_MARGIN + conn.fromBorehole.distance * horizontalScale;
    const x2svg = SVG_LEFT_MARGIN + conn.toBorehole.distance * horizontalScale;

    // Connection point keys
    const pkFT = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, true, conn.connectionId);
    const pkFB = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, true, conn.connectionId);
    const pkTT = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, false, conn.connectionId);
    const pkTB = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, false, conn.connectionId);

    const getAdj = (key, axis) => {
      const adj = connectionPointAdjustments[key];
      if (!adj) return 0;
      return typeof adj === 'object' ? (adj[axis] || 0) : (axis === 'y' ? adj : 0);
    };

    // SVG coordinates (pixels) — same as in GeologicalProfile.jsx rendering
    const svgX1Top = x1svg + getAdj(pkFT, 'x');
    const svgX1Bottom = x1svg + getAdj(pkFB, 'x');
    const svgX2Top = x2svg + getAdj(pkTT, 'x');
    const svgX2Bottom = x2svg + getAdj(pkTB, 'x');

    const svgY1Top = SVG_TOP_MARGIN + conn.fromTop * pixelsPerMeter + getAdj(pkFT, 'y');
    const svgY1Bottom = SVG_TOP_MARGIN + conn.fromBottom * pixelsPerMeter + getAdj(pkFB, 'y');
    const svgY2Top = SVG_TOP_MARGIN + conn.toTop * pixelsPerMeter + getAdj(pkTT, 'y');
    const svgY2Bottom = SVG_TOP_MARGIN + conn.toBottom * pixelsPerMeter + getAdj(pkTB, 'y');

    // Interpolation control points (in SVG pixels)
    const ckTop = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'top', conn.connectionId);
    const ckBottom = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'bottom', conn.connectionId);
    const ckLeft = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'left', conn.connectionId);
    const ckRight = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'right', conn.connectionId);

    const cpTop = interpolationPoints[ckTop] || null;
    const cpBottom = interpolationPoints[ckBottom] || null;
    const cpLeft = interpolationPoints[ckLeft] || null;
    const cpRight = interpolationPoints[ckRight] || null;

    // Sample 4 Bézier edges IN SVG PIXELS, then convert to meters
    const topPtsSvg = sampleCubicBezier(svgX1Top, svgY1Top, svgX2Top, svgY2Top, cpTop, 20);
    const rightPtsSvg = sampleCubicBezier(svgX2Top, svgY2Top, svgX2Bottom, svgY2Bottom, cpRight, 10);
    const bottomPtsSvg = sampleCubicBezier(svgX2Bottom, svgY2Bottom, svgX1Bottom, svgY1Bottom, cpBottom, 20);
    const leftPtsSvg = sampleCubicBezier(svgX1Bottom, svgY1Bottom, svgX1Top, svgY1Top, cpLeft, 10);

    // Combine into closed polygon, remove duplicate endpoints
    const allSvg = [
      ...topPtsSvg,
      ...rightPtsSvg.slice(1),
      ...bottomPtsSvg.slice(1),
      ...leftPtsSvg.slice(1),
    ];

    // Convert to meters
    const meterPts = allSvg.map(p => ({
      x: svgXToM(p.x, horizontalScale),
      y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
    }));

    const colorNum = hexToAci(conn.color);
    addClosedPolyWithHatch(dxf, meterPts, 'POLACZENIA', colorNum, conn.pattern || 'none', 1);
  });
}

// ============================================================
// EKSPORT SOCZEWEK
// ============================================================
function exportLenses(dxf, params) {
  if (!params.showLenses || !params.lenses) return;

  const { maxDepth, pixelsPerMeter, horizontalScale, lenses } = params;

  lenses.forEach(lens => {
    const colorNum = hexToAci(lens.color);

    if (lens.shape === 'circle') {
      const cx = svgXToM(lens.centerX, horizontalScale);
      const cy = svgYToElev(lens.centerY, maxDepth, pixelsPerMeter);
      const rx = lens.radius / horizontalScale;
      const ry = lens.radius / pixelsPerMeter;

      // Aproksymacja elipsy 36-punktowym polyline
      const pts = [];
      const n = 36;
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n;
        pts.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) });
      }
      addClosedPolyWithHatch(dxf, pts, 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5);

      // Nazwa
      if (lens.name) {
        dxf.addText(point3d(cx, cy, 0), 0.3, lens.name, { layerName: 'SOCZEWKI' });
      }

    } else if (lens.shape === 'rectangle') {
      const rectWidth = Math.abs(lens.width);
      const rectHeight = Math.abs(lens.height);
      const rectX = lens.width < 0 ? lens.x + lens.width : lens.x;
      const rectY = lens.height < 0 ? lens.y + lens.height : lens.y;

      const tl = { x: svgXToM(rectX, horizontalScale), y: svgYToElev(rectY, maxDepth, pixelsPerMeter) };
      const tr = { x: svgXToM(rectX + rectWidth, horizontalScale), y: svgYToElev(rectY, maxDepth, pixelsPerMeter) };
      const br = { x: svgXToM(rectX + rectWidth, horizontalScale), y: svgYToElev(rectY + rectHeight, maxDepth, pixelsPerMeter) };
      const bl = { x: svgXToM(rectX, horizontalScale), y: svgYToElev(rectY + rectHeight, maxDepth, pixelsPerMeter) };

      addClosedPolyWithHatch(dxf, [tl, tr, br, bl], 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5);

      // Nazwa
      if (lens.name) {
        const cx = (tl.x + br.x) / 2;
        const cy = (tl.y + br.y) / 2;
        dxf.addText(point3d(cx, cy, 0), 0.3, lens.name, { layerName: 'SOCZEWKI' });
      }

    } else if (lens.shape === 'polygon' && lens.points && lens.points.length >= 3) {
      const mPts = lens.points.map(p => ({
        x: svgXToM(p.x, horizontalScale),
        y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
      }));
      addClosedPolyWithHatch(dxf, mPts, 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5);

      // Nazwa (centroid)
      if (lens.name) {
        const cx = mPts.reduce((s, p) => s + p.x, 0) / mPts.length;
        const cy = mPts.reduce((s, p) => s + p.y, 0) / mPts.length;
        dxf.addText(point3d(cx, cy, 0), 0.3, lens.name, { layerName: 'SOCZEWKI' });
      }
    }
  });
}

// ============================================================
// EKSPORT TOPOGRAFII NMT
// ============================================================
function exportTerrain(dxf, params) {
  if (!params.showTerrain || !params.terrainProfile || params.terrainProfile.length < 2) return;

  // terrainProfile ma już odległość i rzędną w metrach
  const pts = params.terrainProfile;

  dxf.addLWPolyline(
    pts.map(p => ({ point: point2d(p.distance, p.elevation) })),
    { layerName: 'TOPOGRAFIA', colorNumber: Colors.Red }
  );

  // Etykiety rzędnych przy otworach
  params.boreholes.forEach(borehole => {
    const terrainPt = pts.find(p => Math.abs(p.distance - borehole.distance) < 0.5);
    if (terrainPt) {
      dxf.addText(
        point3d(terrainPt.distance, terrainPt.elevation + 0.5, 0),
        0.25,
        `${terrainPt.elevation.toFixed(1)} m n.p.m.`,
        { layerName: 'TOPOGRAFIA', colorNumber: Colors.Red }
      );
    }
  });
}

// ============================================================
// EKSPORT RYSUNKÓW
// ============================================================
function exportDrawings(dxf, params) {
  if (!params.drawings || params.drawings.length === 0) return;

  const { maxDepth, pixelsPerMeter, horizontalScale } = params;

  params.drawings.forEach(d => {
    const x1 = svgXToM(d.startX, horizontalScale);
    const y1 = svgYToElev(d.startY, maxDepth, pixelsPerMeter);
    const x2 = svgXToM(d.endX, horizontalScale);
    const y2 = svgYToElev(d.endY, maxDepth, pixelsPerMeter);
    const colorNum = hexToAci(d.color || '#FF0000');

    switch (d.type) {
      case 'line':
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;

      case 'arrow': {
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        // Grot strzałki
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const arrowLen = 0.3;
        dxf.addLWPolyline([
          { point: point2d(x2, y2) },
          { point: point2d(x2 - arrowLen * Math.cos(angle - Math.PI / 6), y2 - arrowLen * Math.sin(angle - Math.PI / 6)) },
          { point: point2d(x2 - arrowLen * Math.cos(angle + Math.PI / 6), y2 - arrowLen * Math.sin(angle + Math.PI / 6)) },
        ], { flags: LWPolylineFlags.Closed, layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'rect': {
        const minX = Math.min(x1, x2), minY = Math.min(y1, y2);
        const maxX = Math.max(x1, x2), maxY = Math.max(y1, y2);
        dxf.addLWPolyline([
          { point: point2d(minX, minY) },
          { point: point2d(maxX, minY) },
          { point: point2d(maxX, maxY) },
          { point: point2d(minX, maxY) },
        ], { flags: LWPolylineFlags.Closed, layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'circle': {
        const radiusPx = Math.sqrt((d.endX - d.startX) ** 2 + (d.endY - d.startY) ** 2);
        const radiusM = radiusPx / horizontalScale;
        dxf.addCircle(point3d(x1, y1, 0), radiusM, { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'text': {
        const textH = 18 / pixelsPerMeter;
        dxf.addText(point3d(x1, y1, 0), textH, d.text || 'Tekst', { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'measure': {
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        const distM = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        dxf.addText(point3d(midX, midY + 0.2, 0), 0.25, `${distM.toFixed(2)}m`, { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'pencil': {
        if (d.points && d.points.length >= 2) {
          const pts = d.points.map(p => ({
            point: point2d(svgXToM(p.x, horizontalScale), svgYToElev(p.y, maxDepth, pixelsPerMeter)),
          }));
          dxf.addLWPolyline(pts, { layerName: 'RYSUNKI', colorNumber: colorNum });
        }
        break;
      }

      default:
        break;
    }
  });
}

// ============================================================
// EKSPORT FREEDRAW POLYGONÓW
// ============================================================
function exportFreeDrawPolygons(dxf, params) {
  if (!params.connectLayers || !params.manualConnections) return;

  const { maxDepth, pixelsPerMeter, horizontalScale, manualConnections, resolvePointSvg } = params;

  const freeDrawConns = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points);

  freeDrawConns.forEach(mc => {
    const svgPoints = mc.points.map(p => resolvePointSvg(p)).filter(Boolean);
    if (svgPoints.length < 3) return;

    const mPts = svgPoints.map(p => ({
      x: svgXToM(p.x, horizontalScale),
      y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
    }));

    const colorNum = hexToAci(mc.color || '#CCCCCC');
    addClosedPolyWithHatch(dxf, mPts, 'FREEDRAW', colorNum, 'none', 1);
  });
}

// ============================================================
// EKSPORT SIATKI
// ============================================================
function exportGrid(dxf, params) {
  if (!params.showGrid) return;

  const { boreholes, maxDepth } = params;
  if (!boreholes || boreholes.length === 0) return;

  const maxDist = Math.max(...boreholes.map(b => b.distance || 0));
  const maxThickness = Math.max(...boreholes.map(b => getTotalThickness(b.layers)));
  const minElev = maxDepth - maxThickness;

  // Grid w metrach — co 5m
  const gridStep = 5;

  // Vertical lines
  for (let x = 0; x <= maxDist + gridStep; x += gridStep) {
    dxf.addLine(
      point3d(x, maxDepth + 3, 0),
      point3d(x, minElev - 3, 0),
      { layerName: 'SIATKA', colorNumber: 8 }
    );
  }

  // Horizontal lines
  for (let y = Math.floor(minElev) - 3; y <= maxDepth + 3; y += gridStep) {
    dxf.addLine(
      point3d(-2, y, 0),
      point3d(maxDist + gridStep, y, 0),
      { layerName: 'SIATKA', colorNumber: 8 }
    );
  }
}

// ============================================================
// EKSPORT OSI X i Y (skale pionowa i pozioma)
// ============================================================
function exportScales(dxf, params) {
  const { boreholes, maxDepth } = params;
  if (!boreholes || boreholes.length === 0) return;

  const minElev = Math.min(...boreholes.map(b => b.depth - getTotalThickness(b.layers)));
  const sortedBoreholes = [...boreholes].sort((a, b) => a.distance - b.distance);
  const maxDist = Math.max(...boreholes.map(b => b.distance || 0));

  // --- OŚ Y (pionowa) — rzędne, po prawej stronie przekroju ---
  const scaleX = maxDist + 3; // 3m na prawo od ostatniego otworu
  const elevTop = Math.ceil(maxDepth);
  const elevBottom = Math.floor(minElev);
  const range = elevTop - elevBottom;
  const labelInterval = range > 50 ? 10 : 5;

  // Linia główna osi Y
  dxf.addLine(
    point3d(scaleX, elevTop, 0),
    point3d(scaleX, elevBottom, 0),
    { layerName: 'SKALA', colorNumber: 7 }
  );

  // Kreski i etykiety
  for (let elev = elevBottom; elev <= elevTop; elev += 1) {
    const isLabel = elev % labelInterval === 0;
    const tickLen = isLabel ? 0.5 : 0.2;

    dxf.addLine(
      point3d(scaleX - tickLen, elev, 0),
      point3d(scaleX + tickLen, elev, 0),
      { layerName: 'SKALA', colorNumber: 7 }
    );

    if (isLabel) {
      dxf.addText(
        point3d(scaleX + 0.8, elev, 0),
        0.4,
        `${elev}`,
        { layerName: 'SKALA', colorNumber: 7 }
      );
    }
  }

  // Etykieta osi Y
  dxf.addText(
    point3d(scaleX + 0.8, elevTop + 1, 0),
    0.35,
    'm n.p.m.',
    { layerName: 'SKALA', colorNumber: 7 }
  );

  // --- OŚ X (pozioma) — odległości, pod przekrojem ---
  const scaleY = elevBottom - 2; // 2m poniżej najniższego punktu

  if (sortedBoreholes.length > 0) {
    const firstDist = sortedBoreholes[0].distance;
    const lastDist = sortedBoreholes[sortedBoreholes.length - 1].distance;

    // Linia główna osi X
    dxf.addLine(
      point3d(firstDist, scaleY, 0),
      point3d(lastDist, scaleY, 0),
      { layerName: 'SKALA', colorNumber: 7 }
    );

    // Kreski przy otworach + etykiety odległości między nimi
    sortedBoreholes.forEach((borehole, idx) => {
      const x = borehole.distance;

      // Kreska
      dxf.addLine(
        point3d(x, scaleY - 0.5, 0),
        point3d(x, scaleY + 0.5, 0),
        { layerName: 'SKALA', colorNumber: 7 }
      );

      // Nazwa otworu pod osią
      dxf.addText(
        point3d(x, scaleY - 1.2, 0),
        0.3,
        borehole.name || '',
        { layerName: 'SKALA', colorNumber: 7 }
      );

      // Odległość między otworami
      if (idx < sortedBoreholes.length - 1) {
        const nextBorehole = sortedBoreholes[idx + 1];
        const dist = nextBorehole.distance - borehole.distance;
        const midX = (x + nextBorehole.distance) / 2;

        dxf.addText(
          point3d(midX, scaleY + 1, 0),
          0.35,
          `${dist.toFixed(1)}m`,
          { layerName: 'SKALA', colorNumber: 7 }
        );
      }
    });
  }

  // Etykieta osi X
  dxf.addText(
    point3d(maxDist + 1, scaleY - 1.2, 0),
    0.35,
    'odl. [m]',
    { layerName: 'SKALA', colorNumber: 7 }
  );
}

// ============================================================
// DOWNLOAD HELPER
// ============================================================
function downloadDxf(dxfString, filename) {
  const blob = new Blob([dxfString], { type: 'application/dxf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================
// GŁÓWNA FUNKCJA EKSPORTU
// ============================================================
export function exportToDxf({
  boreholes,
  scale,
  horizontalScale,
  connectLayers,
  findLayerConnections,
  interpolationPoints,
  connectionPointAdjustments,
  lenses,
  drawings,
  terrainProfile,
  manualConnections,
  resolvePointSvg,
  // Display toggles
  showGrid,
  showLenses,
  showBoreholeRectangles,
  showDepths,
  showSoilName,
  showSoilState,
  showGeotechnicalLayer,
  showThickness,
  showTerrain,
}) {
  if (!DXF_EXPORT_ENABLED) {
    alert('Eksport DXF jest wyłączony (DXF_EXPORT_ENABLED = false)');
    return;
  }

  if (!boreholes || boreholes.length === 0) {
    alert('Brak otworów do eksportu');
    return;
  }

  const dxf = new DxfWriter();

  // Jednostki = metry
  dxf.setVariable('$INSUNITS', { 70: Units.Meters });

  const pixelsPerMeter = scale / 10;
  const maxDepth = getMaxDepthUtil(boreholes);

  // Tworzenie warstw DXF
  dxf.addLayer('OTWORY', Colors.White);
  dxf.addLayer('WARSTWY_WYPELNIENIE', Colors.Yellow);
  dxf.addLayer('WARSTWY_OPIS', Colors.White);
  dxf.addLayer('POLACZENIA', Colors.Green);
  dxf.addLayer('SOCZEWKI', Colors.Cyan);
  dxf.addLayer('TOPOGRAFIA', Colors.Red);
  dxf.addLayer('SIATKA', Colors.White);
  dxf.addLayer('RYSUNKI', Colors.Magenta);
  dxf.addLayer('GLEBOKOSCI', Colors.White);
  dxf.addLayer('FREEDRAW', Colors.Magenta);
  dxf.addLayer('SKALA', Colors.White);

  const commonParams = {
    boreholes, maxDepth, pixelsPerMeter, horizontalScale,
    connectLayers, findLayerConnections,
    interpolationPoints, connectionPointAdjustments,
    lenses, drawings, terrainProfile,
    manualConnections, resolvePointSvg,
    showGrid, showLenses, showBoreholeRectangles,
    showDepths, showSoilName, showSoilState,
    showGeotechnicalLayer, showThickness, showTerrain,
  };

  // Eksport wszystkich elementów
  exportGrid(dxf, commonParams);
  exportBoreholes(dxf, commonParams);
  exportConnections(dxf, commonParams);
  exportLenses(dxf, commonParams);
  exportTerrain(dxf, commonParams);
  exportDrawings(dxf, commonParams);
  exportFreeDrawPolygons(dxf, commonParams);
  exportScales(dxf, commonParams);

  // Generuj i pobierz
  const dxfString = dxf.stringify();
  downloadDxf(dxfString, 'przekroj_geologiczny.dxf');
}
